/**
 * SICALY: countdown in the announcement bar (snippets/sicaly-countdown.liquid).
 * Counts down in the visitor's own time zone (the browser clock), either to the next local
 * midnight (resets every day) or, when data-end="YYYY-MM-DD" is set, to the end of that day.
 * Past the end date the countdown hides itself. The digits are aria-hidden: screen readers get the
 * static sentence next to them instead of a value that changes every second. Updates stop while
 * the pointer is over the countdown, and the digits are a button that stops it for everyone (WCAG 2.2.2).
 */
class SicalyCountdown extends HTMLElement {
  connectedCallback() {
    this.digits = this.querySelector('[data-digits]');
    this.button = this.querySelector('[data-toggle]');
    this.paused = false;
    this.target = this.#nextTarget();

    if (this.button) {
      this.button.addEventListener('click', this.#toggle);
    }
    this.addEventListener('pointerenter', this.#hoverPause);
    this.addEventListener('pointerleave', this.#hoverResume);

    if (!this.#tick()) return;
    this.toggleAttribute('data-ready', true);
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.button?.removeEventListener('click', this.#toggle);
    this.removeEventListener('pointerenter', this.#hoverPause);
    this.removeEventListener('pointerleave', this.#hoverResume);
  }

  #hoverPause = () => {
    if (!this.paused) this.#stop();
  };

  #hoverResume = () => {
    if (this.paused || this.hidden) return;
    this.#tick();
    this.#start();
  };

  #nextTarget() {
    const end = this.dataset.end;
    if (end && /^\d{4}-\d{2}-\d{2}$/.test(end)) {
      const [year, month, day] = end.split('-').map(Number);
      // End of the chosen day, local time
      return new Date(year, month - 1, day + 1, 0, 0, 0, 0);
    }
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return midnight;
  }

  /** Updates the digits. Returns false when a fixed end date has passed. */
  #tick() {
    let remaining = this.target.getTime() - Date.now();

    if (remaining <= 0) {
      if (this.dataset.end) {
        this.#stop();
        this.hidden = true;
        return false;
      }
      this.target = this.#nextTarget();
      remaining = this.target.getTime() - Date.now();
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value) => String(value).padStart(2, '0');

    const clock = `${pad(hours)}H:${pad(minutes)}M:${pad(seconds)}S`;
    if (this.digits) this.digits.textContent = days > 0 ? `${days}D:${clock}` : clock;
    return true;
  }

  #start() {
    this.#stop();
    this.interval = window.setInterval(() => this.#tick(), 1000);
  }

  #stop() {
    window.clearInterval(this.interval);
  }

  #toggle = () => {
    this.paused = !this.paused;
    if (this.paused) {
      this.#stop();
    } else {
      this.#tick();
      this.#start();
    }
    this.toggleAttribute('paused', this.paused);
    const label = this.paused ? this.button.dataset.resumeLabel : this.button.dataset.pauseLabel;
    this.button.setAttribute('aria-label', label);
    this.button.title = label;
  };
}

if (!customElements.get('sicaly-countdown')) {
  customElements.define('sicaly-countdown', SicalyCountdown);
}
